import { useEffect, useState } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Box,
	Stack,
	Text,
	Center,
	Spinner,
	useToast,
	useColorModeValue,
} from '@chakra-ui/react';
import { ApiProductsMasterGet, mapProductsMasterRow } from 'api/masters/productsList';
import ProductDetailBlock from './ProductDetailBlock';
import { DETAIL_ORDER, ProductMasterRow } from './types';

type Props = {
	product: ProductMasterRow | null;
	isOpen: boolean;
	onClose: () => void;
};

function cell(value: string | undefined | null): string {
	const trimmed = (value ?? '').trim();
	return trimmed || '—';
}

export default function ProductViewUnitsModal({ product, isOpen, onClose }: Props) {
	const toast = useToast();
	const subText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
	const [detail, setDetail] = useState<ProductMasterRow | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!product || !isOpen) {
			setDetail(null);
			return;
		}

		let cancelled = false;
		setLoading(true);
		setDetail(null);

		ApiProductsMasterGet(product.codigo)
			.then((response) => {
				if (cancelled) return;
				setDetail(mapProductsMasterRow(response.data));
			})
			.catch(() => {
				if (cancelled) return;
				setDetail(product);
				toast({
					title: 'No se pudo cargar el detalle completo',
					description: 'Se muestran los datos disponibles del listado.',
					status: 'warning',
					duration: 4000,
				});
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [product, isOpen, toast]);

	if (!product) return null;

	const viewProduct = detail ?? product;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent maxW="90vw">
				<ModalHeader>
					Detalle del producto — {viewProduct.codigo}
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody pb={6}>
					{loading ? (
						<Center py={12}>
							<Spinner size="lg" />
						</Center>
					) : (
						<Stack spacing={4}>
							<Text fontSize="sm" color={subText}>
								{viewProduct.descripcion}
							</Text>
							{DETAIL_ORDER.map((kind) => (
								<ProductDetailBlock
									key={kind}
									kind={kind}
									product={viewProduct}
								/>
							))}
							<Box borderWidth="1px" borderRadius="md" p={3}>
								<Text fontSize="sm" fontWeight="700" mb={2}>
									Unidades de medida
								</Text>
								<Box overflowX="auto">
									<Table size="sm" variant="simple">
										<Thead>
											<Tr>
												<Th>Producto</Th>
												<Th>Código de unidad (Bul o Dis)</Th>
												<Th>Total unidades</Th>
												<Th>Código de barra</Th>
												<Th>Alto</Th>
												<Th>Ancho</Th>
												<Th>Largo</Th>
												<Th>Peso</Th>
											</Tr>
										</Thead>
										<Tbody>
											{viewProduct.unidadesMedida.length === 0 ? (
												<Tr>
													<Td colSpan={8} textAlign="center" color={subText}>
														—
													</Td>
												</Tr>
											) : (
												viewProduct.unidadesMedida.map((u, i) => (
													<Tr key={`${viewProduct.id}-uom-${i}`}>
														<Td>{cell(u.producto)}</Td>
														<Td>{cell(u.codigoUnidad)}</Td>
														<Td>{cell(u.totalUnidades)}</Td>
														<Td>{cell(u.codigoBarra)}</Td>
														<Td>{cell(u.alto)}</Td>
														<Td>{cell(u.ancho)}</Td>
														<Td>{cell(u.largo)}</Td>
														<Td>{cell(u.peso)}</Td>
													</Tr>
												))
											)}
										</Tbody>
									</Table>
								</Box>
							</Box>
						</Stack>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
